<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, watch, getCurrentInstance } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import ModalBox from './ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import AccessTokenService from '@/services/AccessTokenService'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'

const MODAL_ID = 'modalEditAccessToken'

const props = defineProps({
	accessToken: {
		type: Object,
		default: () => ({}),
	},
	modalSize: {
		type: String,
		default: 'md',
		validator(v) {
			return ['xl', 'lg', 'md', 'sm'].some((size) => size === v)
		},
	},
	cancelText: {
		type: String,
		default: 'Cancel',
	},
})

const emit = defineEmits(['token-dismiss', 'modal-cancel', 'modal-dismiss', 'toggle-processing', 'token-edited'])

const { notify } = useNotification()
const root = getCurrentInstance()?.proxy?.$root

const tokenName = ref('')

const { isVisible, hide } = useLegacyModalBridge(MODAL_ID, {
	onShow: () => {
		tokenName.value = props.accessToken?.name ?? ''
	},
	onHidden: () => {
		tokenName.value = ''
	},
})

watch(
	() => props.accessToken?.name,
	(name) => {
		if (isVisible.value && name) {
			tokenName.value = name
		}
	},
)

const dismiss = () => {
	tokenName.value = ''
	emit('token-dismiss')
	emit('modal-cancel')
	hide()
	emit('modal-dismiss')
}

const editToken = async () => {
	try {
		emit('toggle-processing', true)
		hide()
		await AccessTokenService.updateAccessToken(props.accessToken._id, tokenName.value)
		emit('token-edited')
		notify({ group: 'success', text: 'Access token name is updated' })
	} catch (err) {
		notify({
			group: 'error',
			title: "Couldn't edit access token",
			text: err.message,
		})

		if (err.message?.includes('upgrade')) {
			showModal('billing-prompt')
		}
	}
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		:size="modalSize"
		hide-close
		header-class="!hidden"
		wrapper-classes="lg:h-auto lg:max-h-none"
		@close="dismiss"
	>
		<div class="px-6 pt-6">
			<h4 class="text-s-xl">Edit Access Token</h4>
			<p class="text-s-s mb-2 mt-2">Access token name</p>
			<FormInput v-model="tokenName" class="input" placeholder="" />
		</div>
		<template #footer>
			<div class="flex justify-end gap-2 p-6 border-t border-surface-3">
				<FormButton type="surface-4" size="md" @click="dismiss">
					{{ cancelText }}
				</FormButton>
				<FormButton type="main" size="md" @click="editToken">
					Update Token Name
				</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
